import {
  EventIdPublic,
  EventParticipantIdPublic,
  EventSlug,
  MagicLinkIdPublic,
  UserIdPublic,
} from "../schemas/branded-id.schemas.js";

const MARKETING_SITE_BASE_URL = "https://www.planta.co";

function resolveFromParam(options?: {
  from?: string;
  includeFrom?: boolean;
}): string | undefined {
  if (options?.includeFrom === false) return undefined;
  const from = options?.from;
  if (from !== undefined && from !== "") return from;
  return typeof window !== "undefined" ? window.location.href : undefined;
}

export interface EventRouteContext {
  readonly eventIdPublic: EventIdPublic;
  readonly eventSlug?: EventSlug | null | undefined;
  readonly isEventSubdomain: boolean;
}

export interface EventRoutes {
  directory: () => string;
  directoryAll: () => string;
  directoryTopPicks: () => string;
  directoryRequests: () => string;
  directoryConnected: () => string;
  directorySent: () => string;
  participantProfile: (id: EventParticipantIdPublic) => string;
  participantProfileWhyConnect: (id: EventParticipantIdPublic) => string;
  profileEdit: () => string;
  profileEditPreferences: () => string;
  profilePhoto: () => string;
}

function resolveEventIdentifier(ctx: EventRouteContext): string {
  return ctx.eventSlug != null
    ? EventSlug.unwrap(ctx.eventSlug)
    : EventIdPublic.unwrap(ctx.eventIdPublic);
}

export const UserAppRoutes = {
  forEvent(ctx: EventRouteContext): EventRoutes {
    const eId = resolveEventIdentifier(ctx);

    const e = ctx.isEventSubdomain ? "" : `/e/${eId}`;
    const d = `${e}/d`;

    const profileDetailPath = (
      participantIdPublic: EventParticipantIdPublic,
    ): string => {
      const pId = EventParticipantIdPublic.unwrap(participantIdPublic);
      return `${e}/d/profile/${pId}`;
    };

    const profileEditPath = (): string => {
      return `${e}/p/me`;
    };

    return {
      directory(): string {
        return d;
      },

      // Distinct from `directory`, which pins no tab and so opens on whatever the
      // app defaults to — Suggestions wherever recommendations are on. A link
      // meant for the guest list has to say so, or it lands on the deck.
      directoryAll(): string {
        return `${d}?tab=all`;
      },

      directoryTopPicks(): string {
        return `${d}?tab=top-picks`;
      },

      directoryRequests(): string {
        return `${d}?tab=requests`;
      },

      directoryConnected(): string {
        return `${d}?tab=connected`;
      },

      directorySent(): string {
        return `${d}?tab=requests&sent=true`;
      },

      participantProfile(id: EventParticipantIdPublic): string {
        return profileDetailPath(id);
      },

      participantProfileWhyConnect(id: EventParticipantIdPublic): string {
        return `${profileDetailPath(id)}?tab=why-connect`;
      },

      profileEdit(): string {
        return profileEditPath();
      },

      profileEditPreferences(): string {
        return `${profileEditPath()}?tab=preferences`;
      },

      profilePhoto(): string {
        return `${profileEditPath()}/photo`;
      },
    };
  },

  eventLandingPage(ctx: EventRouteContext): string {
    if (ctx.isEventSubdomain) return "/join";
    return `/e/${resolveEventIdentifier(ctx)}/join`;
  },

  userAccount(userIdPublic: UserIdPublic): string {
    return `/u/${UserIdPublic.unwrap(userIdPublic)}`;
  },

  userAccountEvents(userIdPublic: UserIdPublic): string {
    return `/u/${UserIdPublic.unwrap(userIdPublic)}?tab=events`;
  },

  magicLink(magicLinkIdPublic: MagicLinkIdPublic): string {
    return `/m/${encodeURIComponent(MagicLinkIdPublic.unwrap(magicLinkIdPublic))}`;
  },

  auth(): string {
    return "/auth";
  },

  authOtp(otp: string): string {
    return `/auth?otp=${encodeURIComponent(otp)}`;
  },

  terms(options?: { from?: string; includeFrom?: boolean }): string {
    const base = `${MARKETING_SITE_BASE_URL}/terms/`;
    const from = resolveFromParam(options);
    return from !== undefined
      ? `${base}?from=${encodeURIComponent(from)}`
      : base;
  },

  privacy(options?: { from?: string; includeFrom?: boolean }): string {
    const base = `${MARKETING_SITE_BASE_URL}/privacy/`;
    const from = resolveFromParam(options);
    return from !== undefined
      ? `${base}?from=${encodeURIComponent(from)}`
      : base;
  },

  hubTerms(options?: { from?: string; includeFrom?: boolean }): string {
    const base = `${MARKETING_SITE_BASE_URL}/event-hub-terms/`;
    const from = resolveFromParam(options);
    return from !== undefined
      ? `${base}?from=${encodeURIComponent(from)}`
      : base;
  },

  hubPrivacy(options?: { from?: string; includeFrom?: boolean }): string {
    const base = `${MARKETING_SITE_BASE_URL}/event-hub-privacy/`;
    const from = resolveFromParam(options);
    return from !== undefined
      ? `${base}?from=${encodeURIComponent(from)}`
      : base;
  },
};
